/* © CNES */
import { useQueries, useQuery } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch, InputSwitchChangeEvent } from 'primereact/inputswitch';
import { Toolbar } from 'primereact/toolbar';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { restoreArchivedProduct } from '../../../../api/cart/Cart';
import {
  getProcessInfo,
  getQuotasProcess,
  launchProcess,
} from '../../../../api/process/process';
import { ConfigContext } from '../../../../context/ConfigContext';
import { CollectionDetails } from '../../../../models/CollectionDetails';
import { LaunchParams } from '../../../../models/LaunchParams';
import { Process } from '../../../../models/Process';
import { DATASET } from '../../../shared/Constants';
import { isItemAvailable } from '../../../shared/Utils';
import './ProcessDialog.scss';
import { ProcessHeader } from './ProcessHeader/ProcessHeader';

/**
 * Process Props.
 */
interface ProcessProps {
  productsProcess: string[];
  handleVisibleDialog: () => void;
  visibleDialog: boolean;
  setActiveIndex: Function;
  setDoRefetchJobs: Function;
  listProcess: Process[];
  itemsAvailabilitySummary: {
    productId: string;
    available: boolean | undefined;
  }[];
}

/**
 * Process Details.
 */
interface ProcessDetails {
  description?: string;
  inputs?: { key: string; value: string }[];
  outputs?: { key: string; value: string }[];
}

/**
 * ProcessDialog displays a dialog with processing options.
 * @param productsProcess products selected to be processed.
 * @param visibleDialog is the dialog visible.
 * @param handleVisibleDialog Function to toggle visibility of the dialog.
 * @param setActiveIndex Function to change active index in router.
 * @param setDoRefetchJobs Function to refetch jobs when processing products.
 * @param listProcess list of processes that user can choose from.
 * @param itemsAvailabilitySummary summary of items availability on datalake.
 * @returns Element.
 */
export const ProcessDialog = ({
  productsProcess,
  visibleDialog,
  handleVisibleDialog,
  setActiveIndex,
  setDoRefetchJobs,
  listProcess,
  itemsAvailabilitySummary,
}: ProcessProps) => {
  const context = useContext(ConfigContext);
  const navigate = useNavigate();
  const [selectedProcess, setSelectedProcess] = useState<Process | null>(null);
  const [dataReady, setDataReady] = useState<number>(0);
  const [selectedProcessDetails, setSelectedProcessDetails] =
    useState<ProcessDetails | null>(null);
  const [notifEmailChecked, setNotifEmailChecked] = useState<boolean>(false);
  const [showWarningDialog, setShowWarningDialog] = useState<boolean>(false);
  const [showRestaurationDialog, setShowRestaurationDialog] =
    useState<boolean>(false);
  const [showLimiteTraitementDialog, setShowLimiteTraitementDialog] =
    useState<boolean>(false);
  const { data: processInfo } = useQuery(
    [
      'getProcessInfo',
      { processId: selectedProcess?.id },
      navigate,
      context.setError400,
    ],
    getProcessInfo,
    { enabled: !!selectedProcess?.id }
  );
  const [productsWithDataType, setProductsWithDataType] = useState<
    { name: string; dataType: string; id: string }[]
  >([]);
  const productsProcessable = productsWithDataType?.filter((product) => {
    return (
      selectedProcess?.keywords.includes(product.dataType) ||
      selectedProcess?.keywords.includes('test')
    );
  });
  const [productsFinal, setProductsFinal] = useState<
    { name: string; dataType: string; id: string }[]
  >([]);
  const [productNameToRestore, setProductNameToRestore] = useState<string>('');
  const [showQuotaInfoDialog, setShowQuotaInfoDialog] =
    useState<boolean>(false);

  const processProductsQueries = useQueries({
    queries: productsProcessable.map((product) => {
      return {
        queryKey: [
          'launchProcess',
          selectedProcess?.id,
          {
            inputs: {
              'product-title': product.name,
              timeout: 1500,
              'notif-email': notifEmailChecked.toString(),
            },
          } as LaunchParams,
          navigate,
          context.setError400,
          context.setError409,
        ],
        queryFn: launchProcess,
        enabled: false,
      };
    }),
  });

  useEffect(() => {
    if (context.isError409) {
      setShowLimiteTraitementDialog(true);
      context.setError409(false);
    }
    // eslint-disable-next-line
  }, [context.isError409]);

  const { refetch } = useQuery({
    queryKey: [
      'RestoreArchivedProduct',
      {
        payload: {
          product: productNameToRestore,
          process: 'download',
        },
      },
    ],
    queryFn: restoreArchivedProduct,
    enabled: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
  });

  const { data: quotas, refetch: refetchQuotas } = useQuery({
    queryKey: ['Quotas', navigate, context.setError400],
    queryFn: getQuotasProcess,
  });

  /**
   * reset SelectedProcess to null on productsProcess change.
   */
  useEffect(() => {
    setSelectedProcess(null);
  }, [productsProcess]);

  /**
   * launch Processes on click.
   */
  const launchProcesses = async () => {
    refetchQuotas().then((quotas) => {
      if (
        quotas.data?.remainingQuota &&
        quotas.data?.remainingQuota !== -1 &&
        quotas.data?.remainingQuota < productsFinal.length
      ) {
        setShowQuotaInfoDialog(true);
      } else if (quotas.data?.remainingQuota === 0) {
        setShowLimiteTraitementDialog(true);
      } else {
        const launchesSuccess = processProductsQueries.map(async (query) => {
          await query.refetch();
          return query.isSuccess;
        });
        Promise.all(launchesSuccess).then(() => {
          setActiveIndex(1);
          handleVisibleDialog();
          // Wait for Proactive to respond after process execution is acknowlegded.
          setDoRefetchJobs(Boolean(true));
        });
      }
    });
  };

  /**
   * Add dataType information to products on productsProcess change.
   */
  useEffect(() => {
    const newProductsWithDataType = context.productsAdded.collectionsDetails
      .map((collection: CollectionDetails) => {
        return {
          name: collection.productName,
          dataType:
            collection?.collectionDataModel?.productDetails[DATASET] || '',
          id: collection?.id,
        };
      })
      .filter((product) => {
        return productsProcess.includes(product.name);
      });
    setProductsWithDataType(newProductsWithDataType);
    setSelectedProcess(null);
    // eslint-disable-next-line
  }, [productsProcess, listProcess]);

  /**
   * setDataReady with the selected products that have the correct dataType for process.
   */
  useEffect(() => {
    const dataReadyCount = productsFinal.reduce((acc, curr) => {
      let count = acc;
      if (
        selectedProcess?.keywords.includes(curr.dataType) ||
        selectedProcess?.keywords.includes('test')
      ) {
        ++count;
      }
      return count;
    }, 0);
    setDataReady(dataReadyCount);
  }, [productsFinal, selectedProcess]);

  /**
   * setSelectedProcessDetails on processInfo change.
   */
  useEffect(() => {
    if (processInfo) {
      const newProcessDetails: ProcessDetails = {
        description: processInfo.description,
        inputs: Object.keys(processInfo.inputs).map((key) => {
          return { key: key, value: processInfo.inputs[key].description };
        }),
        outputs: Object.keys(processInfo.outputs).map((key) => {
          return { key: key, value: processInfo.outputs[key].description };
        }),
      };
      setSelectedProcessDetails(newProcessDetails);
    }
  }, [processInfo]);

  useEffect(() => {
    if (!!productsProcessable.length) {
      setProductsFinal(
        productsProcessable.filter(
          (product) =>
            itemsAvailabilitySummary.find(
              (item) => item.productId === product.id
            )?.available
        )
      );
    }
    // eslint-disable-next-line
  }, [selectedProcess]);

  const centerContent = (
    <div className="flex align-items-center">
      <p className="DataReady">{dataReady} Data ready</p>
      <Button
        icon="pi pi-cog"
        className="LaunchButton p-button-warning mr-2"
        label="Launch process"
        onClick={() => {
          launchProcesses();
        }}
        disabled={!productsFinal.length}
      />
    </div>
  );

  /**
   * handle Warning Dialog.
   */
  const handleWarningDialog = () => {
    setShowWarningDialog(false);
  };

  /**
   * handle Warning Dialog.
   */
  const handleRestaurationDialog = () => {
    setShowRestaurationDialog(false);
  };

  /**
   * handle Select Process.
   */
  const handleSelectProcess = (e: any) => {
    setSelectedProcess(e.value);
  };

  /**
   * handle Click Restoration Info.
   * @param productName the product name to restore.
   */
  const handleClickRestorationInfo = (productName: string) => {
    setShowRestaurationDialog(true);
    setProductNameToRestore(productName);
  };

  /**
   * handle Click Request Restoration.
   */
  const handleClickRequestRestoration = () => {
    setShowRestaurationDialog(false);
    refetch();
    const collectionsDetailsForRestorationProduct =
      context.productsAdded.collectionsDetails.find(
        (collection) => collection.productName === productNameToRestore
      );
    if (collectionsDetailsForRestorationProduct) {
      const updatedCollection = {
        ...collectionsDetailsForRestorationProduct,
        collectionDataModel: {
          ...collectionsDetailsForRestorationProduct?.collectionDataModel,
          general: {
            ...collectionsDetailsForRestorationProduct?.collectionDataModel
              .general,
            storageMode: 'Datalake Tier 3',
          },
        },
      };

      let previousCollectionsDetails = [
        ...context.productsAdded.collectionsDetails,
      ];

      const index = previousCollectionsDetails.findIndex(
        (collectionsDetails) =>
          collectionsDetails.productName === updatedCollection?.productName
      );

      previousCollectionsDetails[index] = updatedCollection;

      const productsToAdd = {
        ...context.productsAdded,
        collectionDetails: previousCollectionsDetails,
      };

      context.setProductsAdded(productsToAdd);
    }
  };

  /**
   * handle limite traitement Dialog.
   */
  const handleLimiteTraitementDialog = () => {
    setShowLimiteTraitementDialog(false);
    context.setError409(false);
  };

  /**
   * handle close quota information dialog.
   */
  const handleQuotaInfoDialog = () => {
    setShowQuotaInfoDialog(false);
  };
  /**
   * Parse Html string from description.
   * @param htmlString the HTML string to parse.
   * @returns The Html code parsed.
   */
  const htmlParse = (htmlString: string) => {
    const cleanHtmlString = DOMPurify.sanitize(htmlString, {
      USE_PROFILES: { html: true },
    });
    const html = parse(cleanHtmlString);
    return html;
  };
  return (
    <Dialog
      visible={visibleDialog}
      headerStyle={{ color: 'rgba(0, 45, 128, 1)' }}
      onHide={handleVisibleDialog}
      className="ContainerProcess"
    >
      <Button
        label="MyData"
        icon="pi pi-arrow-left"
        className="BackMyData p-button-warning mr-2"
        onClick={handleVisibleDialog}
      />
      <ProcessHeader />

      <div className="dialog-body">
        <div className="titleSelect">
          <p>Select a process</p>
        </div>
        <div className="ProcessContainer">
          <Dropdown
            value={selectedProcess}
            onChange={handleSelectProcess}
            options={
              listProcess?.filter(
                (el: Process) => !el.keywords.includes('INTERNAL')
              ) || []
            }
            optionLabel="title"
            placeholder="Select a Process"
            className="SelectProcess"
          />
          <div className="RequestMessage">
            <span className="text-question">
              Receive a notification of the result by email ?
            </span>
            <label>No</label>
            <InputSwitch
              checked={notifEmailChecked}
              onChange={(e: InputSwitchChangeEvent) =>
                setNotifEmailChecked(Boolean(e.value))
              }
              className="InputSwitch"
            />
            <label>Yes</label>
          </div>
        </div>
        <div className="ItemsToProcess">
          {selectedProcess && (
            <div className="process-description">
              <span className="icon-description pi pi-info-circle "></span>
              <p className="description-text">
                <b>Description :</b>{' '}
                {selectedProcessDetails?.description
                  ? htmlParse(selectedProcessDetails?.description)
                  : 'no description found'}
              </p>
              <div className="moreInfo">
                <a
                  href={`${window.__RUNTIME_CONFIG__?.PROCESSINGS_URL}`}
                  target="_blank"
                  rel="noreferrer"
                  className="lienTitle"
                >
                  More info
                </a>
              </div>
            </div>
          )}
          {!!productsWithDataType.length && (
            <div className="listItems">
              {productsWithDataType.map((product, index) => {
                return (
                  <div className="cardItems" key={product.name}>
                    <Card>
                      <div
                        className={
                          !!selectedProcess &&
                          isItemAvailable(
                            itemsAvailabilitySummary,
                            product.id
                          ) &&
                          (selectedProcess?.keywords.includes(
                            product.dataType
                          ) ||
                            selectedProcess?.keywords.includes('test'))
                            ? 'card-item-process-applyable'
                            : 'card-item-process-non-applyable'
                        }
                      >
                        <div className="title">
                          <p>{product.name}</p>
                        </div>
                        <div className="warning-icon">
                          {!isItemAvailable(
                            itemsAvailabilitySummary,
                            product.id
                          ) && (
                            <Button
                              aria-label="ButtonForWarning"
                              name="ButtonForWarning"
                              onClick={(e: React.MouseEvent) =>
                                handleClickRestorationInfo(product.name)
                              }
                              className="ButtonForWarning"
                              icon={
                                <i
                                  className="pi pi-cloud-download"
                                  style={{
                                    fontSize: '25px',
                                  }}
                                />
                              }
                              rounded
                              text
                              severity="warning"
                              tooltipOptions={{ position: 'left' }}
                              tooltip="Restore"
                            />
                          )}
                          {!!selectedProcess &&
                            !selectedProcess?.keywords.includes(
                              product.dataType
                            ) &&
                            !selectedProcess?.keywords.includes('test') && (
                              <Button
                                onClick={(e: React.MouseEvent) =>
                                  setShowWarningDialog(true)
                                }
                                className="ButtonForWarning"
                                icon={
                                  <img
                                    alt="warning icon"
                                    src={require('./warning_item_icon.png')}
                                    style={{
                                      background: 'rgba(239, 74, 17, 1)',
                                    }}
                                  />
                                }
                                rounded
                                text
                                severity="warning"
                                tooltipOptions={{ position: 'left' }}
                                tooltip="See what's wrong"
                              />
                            )}
                        </div>
                      </div>
                    </Card>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <div className="ToolbarProcessContainer">
        <Toolbar center={centerContent} />
      </div>
      <Dialog
        visible={showWarningDialog}
        headerStyle={{ color: 'rgba(0, 45, 128, 1)' }}
        onHide={handleWarningDialog}
        className="WarningDialog"
        header="Process Not Applyable"
      >
        <div className="WarningDialogContent">
          <div className="DialogRow">
            <img
              alt="warning icon"
              src={require('./warning_item_icon.png')}
              style={{
                background: 'rgba(239, 74, 17, 1)',
                height: '25px',
                marginRight: '1rem',
              }}
            />
            <p>
              This data cannot be assigned to the selected Process. It will be
              ignored.
            </p>
          </div>

          <div className="moreInfo">
            <a
              href={window.__RUNTIME_CONFIG__?.PROCESSINGS_URL}
              target="_blank"
              rel="noreferrer"
              className="lienTitle"
            >
              More info
            </a>
          </div>
        </div>
      </Dialog>
      <Dialog
        visible={showRestaurationDialog}
        headerStyle={{ color: 'rgba(0, 45, 128, 1)' }}
        onHide={handleRestaurationDialog}
        className="WarningDialog"
        header="Data Needs To Be Recovered From Datalake"
      >
        <div className="WarningDialogContent">
          <div className="DialogRow">
            <p>
              This data cannot be assigned to the selected Process. It will be
              ignored.
            </p>
          </div>

          <div className="moreInfo">
            {' '}
            <Button
              icon="pi pi-cloud-download"
              className="LaunchButton p-button-warning mr-2"
              label="Restore product"
              onClick={() => {
                handleClickRequestRestoration();
              }}
            />
          </div>
        </div>
      </Dialog>
      <Dialog
        visible={showLimiteTraitementDialog}
        headerStyle={{ color: 'red' }}
        onHide={handleLimiteTraitementDialog}
        className="WarningDialog"
        header="Processing cannot execute"
      >
        <div className="WarningDialogContent">
          <div className="DialogRow">
            <p>
              Your request exceeds the amount of processings you are allowed to
              execute at once. Please wait for your former processings to end.
              You can consult your detailed quota in My Profile.
            </p>
          </div>
        </div>
      </Dialog>
      <Dialog
        visible={showQuotaInfoDialog}
        headerStyle={{ color: 'red' }}
        onHide={handleQuotaInfoDialog}
        className="WarningDialog"
        header="Too many processings"
      >
        <div className="WarningDialogContent">
          <div className="DialogRow">
            <p>
              You have selected too many products and it exceeds the amount of
              processings you are allowed to execute at once. Please wait for
              your former processings to end or reduce the amount of data you
              are trying to process to {quotas?.remainingQuota} products. You
              can consult your detailed quota in My Profile.
            </p>
          </div>
        </div>
      </Dialog>
    </Dialog>
  );
};
